import React, { Attributes, Component, RefAttributes, createRef } from 'react';
import { createRoot } from 'react-dom/client';
import { Logger } from '../../../models/logger';
import { OptionalChildrenProps } from '../../../types/react.types';
import { BrowserUtils } from '../../../ui-utils/browser-utils';
import style from './modal.component.module.scss';

export interface ModalProps extends OptionalChildrenProps {
    modalClass?: string;
    onOpen?: (modalRef: Modal) => void;
    onClose?: (modalRef: Modal) => void;
    disableCloseButton?: boolean;
    /** `alertdialog` for a message that must be answered (AlertDialog); default `dialog`. */
    role?: 'dialog' | 'alertdialog';
    /** Id of the element that names the dialog when its title is not a `data-modalheader` (AlertDialog's h2). */
    labelledBy?: string;
    /** Id(s) of the element(s) that describe the dialog (AlertDialog's message). */
    describedBy?: string;
    /**
     * Escape on a dialog without a close key (`disableCloseButton`): the caller decides what it means (AlertDialog maps it
     * to its No/cancel answer). Without it such a dialog ignores Escape, as before.
     */
    onEscape?: () => void;
    /**
     * UXFU2-03: where focus goes on close when there is no opener to go back to -- the dialog opened by itself (an
     * upload failure arrives in the background, with focus on <body>) or the opener left the page. Read at close, so it
     * can answer for the page as it is then (the failed row's Retry, or the file picker once the row is retrying). Null,
     * a node off the page, or a throw: focus stays where the browser put it, as before.
     */
    returnFocusTo?: () => HTMLElement | null | undefined;
}

export interface ModalState {
    shown: boolean;
}

/** What Tab can reach inside a dialog (UX3-05 focus trap). */
const FOCUSABLE = [
    'a[href]', 'area[href]', 'button:not([disabled])', 'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])', 'textarea:not([disabled])', 'iframe', '[contenteditable="true"]',
    '[tabindex]:not([tabindex="-1"])',
].join(',');

let modalSeq = 0;

export default class Modal extends React.Component<ModalProps, ModalState> {
    private static readonly logger = Logger.getLogger('Modal');

    static createModal<T>(Comp: any, props: any): any {
        if (!BrowserUtils.hasDocument()) {
            Modal.logger.error("Requested execution aborted since document was not avalilable");
            return;
        }
        const modalPortal = document.createElement('div');
        modalPortal.className = `${Date.now()}`;
        modalPortal.style.position = 'relative';
        modalPortal.style.zIndex = '9999';
        document.body.appendChild(modalPortal);

        const child = <Comp {...props}></Comp>;
        const root = createRoot(modalPortal!);
        root.render(child);
        return child;
    }
    parentElement;
    setParentElement = (element) => {
        this.parentElement = element && element.parentElement;
    }

    readonly state: Readonly<ModalState> = {
        shown: true,
    };

    // ---- UX3-05 (prod fixes 2026-10-02): dialog semantics, focus in, Tab kept inside, Escape, focus back ----------
    /** The dialog box itself (role=dialog); focused on open when nothing inside has focus yet. */
    private readonly dialogRef = createRef<HTMLDivElement>();
    private readonly titleId = `modal-title-${++modalSeq}`;
    /** What had focus when the dialog was asked for (the card's Re-edit key, ...); it gets focus back on close. */
    private opener: HTMLElement | null = null;
    private focusReturned = false;

    constructor(props: ModalProps) {
        super(props);
        if (BrowserUtils.hasDocument()) {
            const active = document.activeElement as HTMLElement | null;
            this.opener = active && active !== document.body ? active : null;
        }
    }

    componentDidMount() {
        this.moveFocusIn();
        if (this.props.onOpen) {
            this.props.onOpen(this);
        }
    }

    componentWillUnmount() {
        this.returnFocus();
    }

    /** Focus moves into the dialog on open, unless a child already put it there (an alert's own button, a form field). */
    private moveFocusIn() {
        const dialog = this.dialogRef.current;
        if (!dialog || !BrowserUtils.hasDocument()) return;
        if (dialog.contains(document.activeElement)) return;
        dialog.focus({ preventScroll: true });
    }

    /**
     * Back to the opener, if it is still on the page and focus is not somewhere the user put it since; with no opener on
     * the page, to the caller's `returnFocusTo` (UXFU2-03).
     */
    private returnFocus() {
        if (this.focusReturned || !BrowserUtils.hasDocument()) return;
        this.focusReturned = true;
        const opener = this.opener && this.opener.isConnected ? this.opener : null;
        if (!opener && !this.props.returnFocusTo) return;
        const active = document.activeElement;
        const dialog = this.dialogRef.current;
        if (active && active !== document.body && !(dialog && dialog.contains(active))) return;
        const target = opener ?? this.fallbackFocusTarget();
        if (!target) return;
        try {
            target.focus({ preventScroll: true });
        } catch (e) {
            Modal.logger.warn({ message: 'Could not return focus to the dialog opener', error: e });
        }
    }

    private fallbackFocusTarget(): HTMLElement | null {
        try {
            const target = this.props.returnFocusTo?.();
            return target && target.isConnected ? target : null;
        } catch (e) {
            Modal.logger.warn({ message: 'Could not resolve where focus goes after the dialog', error: e });
            return null;
        }
    }

    private focusables(): HTMLElement[] {
        const dialog = this.dialogRef.current;
        if (!dialog) return [];
        return (Array.from(dialog.querySelectorAll(FOCUSABLE)) as HTMLElement[]).filter((el) => {
            if (el.getAttribute('aria-hidden') === 'true' || el.closest('[hidden], [inert]')) return false;
            const visible = (el as any).checkVisibility;
            return typeof visible === 'function' ? visible.call(el) : true;
        });
    }

    private onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.key === 'Escape' || e.key === 'Esc') {
            // An Escape a control inside already handled (a menu closing itself) is that control's, and one pressed while
            // an IME is composing (CJK input) cancels the composition, not the dialog (UX4-05).
            if (e.defaultPrevented || e.nativeEvent?.isComposing || e.keyCode === 229) return;
            // A dialog without a close key (an alert that must be answered) is not dismissed by Escape either, unless
            // its caller says what Escape answers (UX4-04: AlertDialog's No).
            if (this.props.disableCloseButton) {
                if (!this.props.onEscape) return;
                e.stopPropagation();
                e.preventDefault();
                this.props.onEscape();
                return;
            }
            e.stopPropagation();
            e.preventDefault();
            this.unmount();
            return;
        }
        if (e.key !== 'Tab') return;
        const items = this.focusables();
        const dialog = this.dialogRef.current;
        if (!dialog) return;
        if (items.length === 0) {
            e.preventDefault();
            dialog.focus({ preventScroll: true });
            return;
        }
        const first = items[0];
        const last = items[items.length - 1];
        const active = document.activeElement as HTMLElement | null;
        const inside = !!active && dialog.contains(active);
        if (e.shiftKey) {
            if (!inside || active === first || active === dialog) {
                e.preventDefault();
                last.focus();
            }
        } else if (!inside || active === last) {
            e.preventDefault();
            first.focus();
        }
    };

    private getHeader() {
        if (Array.isArray(this.props.children)) {
            return this.props.children.find((child: any) => child && child.props && child.props['data-modalheader']);
        }
        return '';
    }

    private getBody() {
        if (Array.isArray(this.props.children)) {
            return this.props.children.find((child: any) => child && child.props && child.props['data-modalbody']);
        }
        return '';
    }

    private getFooter() {
        if (Array.isArray(this.props.children)) {
            return this.props.children.find((child: any) => child && child.props && child.props['data-modalfooter']);
        }
        return '';
    }

    public unmount() {
        this.setState({
            shown: false,
        });
        if (this.props.onClose) {
            this.props.onClose(this);
        }
        try {
            if (this.parentElement) {
                this.parentElement.remove();
            }
        } catch (e) {
            Modal.logger.error({ message: 'Error while removing modal', error: e });
        }
        // UX3-05: Cancel, the close key, Escape and every other way out land the user back where they were.
        this.returnFocus();
    }

    render() {
        const header = this.getHeader();
        return (this.state.shown &&
            <div ref={(ref) => this.setParentElement(ref)} className={`${this.props.modalClass} modal ${style.modal}`}
                onKeyDown={this.onKeyDown}>
                <div ref={this.dialogRef} className={`content ${style.content}`}
                    role={this.props.role || 'dialog'} aria-modal="true"
                    aria-labelledby={this.props.labelledBy || (header ? this.titleId : undefined)}
                    aria-describedby={this.props.describedBy || undefined} tabIndex={-1}>
                    <div className={`head ${style.head}`}>
                        <div id={this.titleId} className={`header-content ${style['header-content']}`}>{header}</div>
                        {
                            !this.props.disableCloseButton &&
                            <button type="button" aria-label="Close" className={`close ${style.close}`} onClick={() => this.unmount()}
                                onTouchEnd={() => this.unmount()}>&#x2716;</button>
                        }
                    </div>
                    <div className={`body ${style.body}`}>{this.getBody()}</div>
                    <div className={`footer ${style.footer}`}>{this.getFooter()}</div>
                </div>
            </div>
        );
    }
}